import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SECURITY: session lives in an httpOnly cookie (XSS-safe). Send it with every request.
axios.defaults.withCredentials = true;

// Marker stored in localStorage instead of the real JWT (kept for legacy guards in pages)
const COOKIE_SESSION = 'cookie';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If returning from Emergent OAuth callback, skip the /me check.
    // AuthCallback will exchange session_id and set the session cookie first.
    if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) {
      setLoading(false);
      return;
    }
    initSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initSession = async () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken && storedToken !== COOKIE_SESSION) {
      // Legacy JWT still in localStorage: upgrade it to an httpOnly cookie session
      try {
        await axios.post(`${API}/auth/session/upgrade`, {}, {
          headers: { Authorization: `Bearer ${storedToken}` }
        });
        localStorage.setItem('token', COOKIE_SESSION);
        setToken(COOKIE_SESSION);
      } catch (e) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setLoading(false);
        return;
      }
    }
    await fetchUser();
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      localStorage.setItem('token', COOKIE_SESSION);
      setToken(COOKIE_SESSION);
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData } = response.data;

    // JWT is set as httpOnly cookie by the backend - nothing sensitive stored client-side
    localStorage.setItem('token', COOKIE_SESSION);
    setToken(COOKIE_SESSION);
    setUser(userData);

    return userData;
  };

  const register = async (email, password, name, promo_code = null) => {
    // Pick up affiliate code from /r/{code} landing page (30-day cookie in localStorage)
    let affiliate_code = null;
    try {
      const stored = localStorage.getItem('tradex_affiliate');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.code && parsed?.expires_at && new Date(parsed.expires_at) > new Date()) {
          affiliate_code = parsed.code;
        }
      }
    } catch (_) { /* ignore */ }

    // Pick up "Devenir Fondateur" intent stashed on /subscribe when the user was not logged in
    let founder_intent = false;
    try {
      founder_intent = localStorage.getItem('tradex_founder_intent') === '1';
    } catch (_) { /* ignore */ }

    const response = await axios.post(`${API}/auth/register`, {
      email, password, name, affiliate_code, promo_code, founder_intent,
    });
    const { user: userData } = response.data;

    localStorage.setItem('token', COOKIE_SESSION);
    setToken(COOKIE_SESSION);
    setUser(userData);

    // Consume the founder intent flag now that the account has been created
    try { localStorage.removeItem('tradex_founder_intent'); } catch (_) { /* ignore */ }

    // If founder intent was set, the backend upgraded the tier server-side —
    // refresh from /auth/me so the header + gating reflect the new Founder Expert status.
    if (founder_intent) {
      try { await fetchUser(); } catch (_) { /* ignore */ }
    }

    // Check if there's a risk profile from questionnaire in localStorage
    const savedProfile = localStorage.getItem('tradex_risk_profile');
    const savedScore = localStorage.getItem('tradex_risk_score');
    if (savedProfile && savedScore) {
      try {
        await axios.put(`${API}/auth/risk-profile`, {
          risk_profile: savedProfile,
          risk_score: parseInt(savedScore)
        });
        setUser({ ...userData, risk_profile: savedProfile, risk_score: parseInt(savedScore) });
        localStorage.removeItem('tradex_risk_profile');
        localStorage.removeItem('tradex_risk_score');
      } catch (error) {
        console.error('Failed to save risk profile:', error);
      }
    }

    return userData;
  };

  const updateRiskProfile = async (riskProfile, riskScore) => {
    try {
      await axios.put(`${API}/auth/risk-profile`, {
        risk_profile: riskProfile,
        risk_score: riskScore
      });
      setUser(prev => ({ ...prev, risk_profile: riskProfile, risk_score: riskScore }));
      return true;
    } catch (error) {
      console.error('Failed to update risk profile:', error);
      return false;
    }
  };

  const useFreeAnalysis = async (analysisType) => {
    try {
      const response = await axios.post(`${API}/auth/use-free-analysis?analysis_type=${analysisType}`);
      setUser(prev => ({ ...prev, free_analyses: response.data.free_analyses }));
      return { success: true, remaining: response.data.total_remaining };
    } catch (error) {
      console.error('Failed to use free analysis:', error);
      return { success: false, error: error.response?.data?.detail || 'Error' };
    }
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Cookie clearing failed server-side; still clear client state
    }
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateRiskProfile, useFreeAnalysis, refreshUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
